import '@ungap/custom-elements';
import {defineCustomElement} from '../utils/defineCustomElement.js';
import {
    TIME_24H_FORMAT,
    TIME_12H_FORMAT,
    TIME_AUTO_FORMAT,
    DATE_FORMAT,
    DATE_WEEKDAY_FORMAT,
    MONTH_FORMAT,
    DAY_FORMAT,
    DATETIME_24H_FORMAT,
    MONTH_DAY_TIME_AUTO_FORMAT
} from '../utils/timeFormat.js';
import {escapeHtml} from '../utils/markupHelpers.js';
import {t} from '../localization/translate.js';
import {documentLocale} from '../localization/documentLocale.js';
import {detectTimezone} from '../modules/timezone.js';

/**
 * Build the HTML content used by meetup card local-time range popovers.
 * @param {string} localStartTime
 * @param {string} localEndTime
 * @param {string} cityName
 * @returns {string}
 */
export function buildLocalTimeRangePopoverHtml(localStartTime, localEndTime, cityName) {
    return `<strong>${escapeHtml(t('At :start-:end your local time (:city).', {start: localStartTime, end: localEndTime, city: cityName}))}</strong>`;
}

/**
 * The start and end of a range in the browser's time zone. An end on another local day carries its date,
 * so a meetup that runs over several days never reads as a short slot.
 * @param {Date} start
 * @param {Date} end
 * @returns {{start: string, end: string}}
 */
export function formatLocalTimeRange(start, end) {
    const endsOnStartDay = start.toDateString() === end.toDateString();

    return {
        start: formatLocal(start, TIME_AUTO_FORMAT),
        end: formatLocal(end, endsOnStartDay ? TIME_AUTO_FORMAT : MONTH_DAY_TIME_AUTO_FORMAT),
    };
}

/**
 * A custom element that displays dates/times in the user's local timezone.
 * Provides a Single Source of Truth (SSoT) for local time formatting across the application.
 *
 * @example
 * <!-- Simple time display -->
 * <time datetime="2025-12-16T15:30:00Z" is="ixdf-local-time">15:30 UTC</time>
 *
 * @example
 * <!-- Time with format -->
 * <time datetime="2025-12-16T15:30:00Z" is="ixdf-local-time" format="time">15:30 UTC</time>
 *
 * @example
 * <!-- Full datetime -->
 * <time datetime="2025-12-16T15:30:00Z" is="ixdf-local-time" format="datetime">Dec 16, 2025 15:30 UTC</time>
 *
 * @example
 * <!-- With popover showing both local and UTC time -->
 * <time datetime="2025-12-16T15:30:00Z" is="ixdf-local-time" format="popover" data-utc-time="15:30 UTC">
 *     15:30 UTC
 * </time>
 *
 * @example
 * <!-- Time range; the end carries its date when it falls on another local day -->
 * <time datetime="2025-12-16T15:30:00Z" data-end-datetime="2025-12-18T18:30:00Z" is="ixdf-local-time" format="time-range">15:30 – Dec 18, 18:30 UTC</time>
 *
 * @example
 * <!-- Time range popover for meetup cards -->
 * <time datetime="2025-12-16T15:30:00Z" data-end-datetime="2025-12-16T18:30:00Z" is="ixdf-local-time" format="time-range-popover">
 *     ...
 * </time>
 */
class LocalTimeElement extends HTMLTimeElement {
    connectedCallback() {
        // A browser can report no time zone; its local times are then of an unknown zone, so the server-rendered UTC text stays.
        if (!localTimeZone()) {
            return;
        }

        const date = this.#parseDate();
        const format = this.getAttribute('format') || 'time';

        switch (format) {
            case 'time':
                this.textContent = formatLocal(date, TIME_24H_FORMAT);
                break;
            case 'time12h':
                this.textContent = formatLocal(date, TIME_12H_FORMAT);
                break;
            case 'time-auto':
                this.textContent = formatLocal(date, TIME_AUTO_FORMAT);
                break;
            case 'date':
                this.textContent = formatLocal(date, DATE_FORMAT);
                break;
            case 'date-weekday':
                this.textContent = formatLocal(date, DATE_WEEKDAY_FORMAT);
                break;
            case 'month':
                this.textContent = formatLocal(date, MONTH_FORMAT);
                break;
            case 'day':
                this.textContent = formatLocal(date, DAY_FORMAT);
                break;
            case 'datetime':
                this.textContent = formatLocal(date, DATETIME_24H_FORMAT);
                break;
            case 'datetime-auto':
                this.textContent = t(':date. At :time your local time (:city)', {date: formatLocal(date, DATE_FORMAT), time: formatLocal(date, TIME_AUTO_FORMAT), city: localCityName()});
                break;
            case 'popover': {
                const utcTime = this.dataset.utcTime || utcFormatter().format(date);
                const localTime = t('At :time your local time (:city).', {time: formatLocal(date, TIME_AUTO_FORMAT), city: localCityName()});
                this.#updatePopoverDescription(`<strong>${escapeHtml(localTime)}</strong><br>${escapeHtml(t('In UTC: :time.', {time: utcTime}))}`);
                break;
            }
            case 'time-range': {
                const range = formatLocalTimeRange(date, this.#parseDate('endTimestamp', 'endDatetime'));
                this.textContent = `${range.start} – ${range.end}`;
                break;
            }
            case 'time-range-popover': {
                const range = formatLocalTimeRange(date, this.#parseDate('endTimestamp', 'endDatetime'));
                this.#updatePopoverDescription(buildLocalTimeRangePopoverHtml(range.start, range.end, localCityName()));
                break;
            }
            default:
                throw new Error(`ixdf-local-time: unknown format "${format}".`);
        }
    }

    /**
     * An invalid date throws, so the server-rendered fallback stays on the page instead of "Invalid Date".
     * @param {string} timestampKey dataset key holding Unix seconds
     * @param {string} datetimeKey dataset key holding an ISO string; the start date reads the `datetime` attribute
     * @returns {Date}
     */
    #parseDate(timestampKey = 'timestamp', datetimeKey = null) {
        const timestamp = this.dataset[timestampKey];
        const datetime = datetimeKey ? this.dataset[datetimeKey] : this.getAttribute('datetime');
        const date = timestamp ? new Date(Number(timestamp) * 1000) : new Date(datetime ?? NaN);

        if (Number.isNaN(date.getTime())) {
            throw new Error(`ixdf-local-time: no valid date in data-${timestampKey} or ${datetimeKey ? 'data-' + datetimeKey : 'datetime'} ("${timestamp ?? datetime}").`);
        }

        return date;
    }

    #updatePopoverDescription(descriptionHtml) {
        const textContainer = this.querySelector('[data-popover-bubble] [data-popover-description]');
        if (!textContainer) {
            throw new Error('ixdf-local-time popover formats require a data-popover-bubble with a data-popover-description inside.');
        }

        textContainer.innerHTML = descriptionHtml;
    }
}

// A formatter is expensive to build and a page can hold dozens of times (meetup and poll lists), so build one per format.
/** @type {Map<Intl.DateTimeFormatOptions, Intl.DateTimeFormat>} */
const localFormatters = new Map();

/**
 * @param {Date} date
 * @param {Intl.DateTimeFormatOptions} options one of the shared constants from timeFormat.js
 * @returns {string}
 */
function formatLocal(date, options) {
    let formatter = localFormatters.get(options);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(undefined, options);
        localFormatters.set(options, formatter);
    }

    return formatter.format(date);
}

/** @type {Intl.DateTimeFormat|null} */
let utcTimeFormatter = null;

/** @returns {Intl.DateTimeFormat} the UTC time line of the 'popover' format */
function utcFormatter() {
    utcTimeFormatter ??= new Intl.DateTimeFormat(documentLocale(), {...TIME_AUTO_FORMAT, timeZone: 'UTC'});

    return utcTimeFormatter;
}

/** @type {string|undefined|null} null until read; undefined when the browser reports no time zone */
let timeZone = null;

/** @returns {string|undefined} */
function localTimeZone() {
    if (timeZone === null) {
        timeZone = detectTimezone();
    }

    return timeZone;
}

/**
 * The city part of the browser's time zone ("Europe/Madrid" gives "Madrid", "America/New_York" gives "New York").
 * @returns {string}
 */
function localCityName() {
    const zone = localTimeZone();

    return zone.includes('/') ? zone.split('/').pop().replaceAll('_', ' ') : zone;
}

defineCustomElement('ixdf-local-time', LocalTimeElement, {extends: 'time'});

export {LocalTimeElement};
